import { useEffect } from 'react';

interface UsePlaybackStateSyncProps {
  audioRef: HTMLAudioElement | null;
  videoRef: HTMLVideoElement | null;
  setIsPlaying: (isPlaying: boolean) => void;
  isSeekingRef?: React.MutableRefObject<boolean>;
}

export const usePlaybackStateSync = ({
  audioRef,
  videoRef,
  setIsPlaying,
  isSeekingRef,
}: UsePlaybackStateSyncProps) => {
  useEffect(() => {
    if (!audioRef) return;

    const handlePlayEvent = () => {
      setTimeout(() => {
        if (isSeekingRef?.current) return;
        
        if (audioRef && !audioRef.paused) {
          setIsPlaying(true);
          if (videoRef) {
            videoRef.play().catch(() => {
              console.warn('Video play failed, continuing with audio only');
            });
          }
        }
      }, 0);
    };

    const handlePauseEvent = () => {
      if (isSeekingRef?.current) return;
      
      setIsPlaying(false);
      if (videoRef) {
        try {
          videoRef.pause();
        } catch (error) {
          console.error('Error pausing video in playback sync:', error);
        }
      }
      setTimeout(() => {
        if (audioRef && audioRef.paused) {
          setIsPlaying(false);
        }
      }, 50);
    };

    const handlePlayingEvent = () => {
      if (isSeekingRef?.current) return;
      
      if (audioRef && !audioRef.paused) {
        setIsPlaying(true);
        if (videoRef && videoRef.paused) {
          videoRef.play().catch(() => {
            console.warn('Video play failed, continuing with audio only');
          });
        }
      }
    };

    audioRef.addEventListener('play', handlePlayEvent);
    audioRef.addEventListener('pause', handlePauseEvent);
    audioRef.addEventListener('playing', handlePlayingEvent);

    return () => {
      audioRef.removeEventListener('play', handlePlayEvent);
      audioRef.removeEventListener('pause', handlePauseEvent);
      audioRef.removeEventListener('playing', handlePlayingEvent);
    };
  }, [audioRef, videoRef, setIsPlaying, isSeekingRef]);
};
